const currencyStorageKey = "leobei:currency";

function setupTheme(): void {
  const button = document.querySelector<HTMLButtonElement>("[data-theme-toggle]");
  if (!button) return;
  const sync = () => button.setAttribute("aria-pressed", String(document.documentElement.classList.contains("dark")));
  sync();
  button.addEventListener("click", () => {
    const dark = document.documentElement.classList.toggle("dark");
    try { localStorage.setItem("fd-theme", dark ? "dark" : "light"); } catch { /* Storage can be unavailable in privacy mode. */ }
    sync();
  });
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || "fr", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount).toLocaleString()} ${currency}`;
  }
}

function normalizeComboboxSearch(value: string): string {
  return value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase();
}

function setupComboboxes(): void {
  const roots = [...document.querySelectorAll<HTMLElement>("[data-combobox-root]")];
  const close = (root: HTMLElement, restoreFocus = false) => {
    const trigger = root.querySelector<HTMLButtonElement>("[data-combobox-trigger]");
    const popover = root.querySelector<HTMLElement>("[data-combobox-popover]");
    if (!trigger || !popover) return;
    trigger.setAttribute("aria-expanded", "false");
    popover.hidden = true;
    if (restoreFocus) trigger.focus();
  };
  const applyFilter = (root: HTMLElement) => {
    const search = root.querySelector<HTMLInputElement>("[data-combobox-search]");
    const query = normalizeComboboxSearch(search?.value.trim() || "");
    const options = [...root.querySelectorAll<HTMLElement>("[data-combobox-option]")];
    let visible = 0;
    options.forEach((option) => {
      option.hidden = Boolean(query) && !normalizeComboboxSearch(option.dataset.search || option.textContent || "").includes(query);
      if (!option.hidden) visible += 1;
    });
    const empty = root.querySelector<HTMLElement>("[data-combobox-empty]");
    if (empty) empty.hidden = visible !== 0;
  };

  roots.forEach((root) => {
    const trigger = root.querySelector<HTMLButtonElement>("[data-combobox-trigger]");
    const popover = root.querySelector<HTMLElement>("[data-combobox-popover]");
    const search = root.querySelector<HTMLInputElement>("[data-combobox-search]");
    if (!trigger || !popover || !search) return;

    trigger.addEventListener("click", () => {
      const opening = popover.hidden;
      roots.forEach((other) => close(other));
      if (!opening) return;
      trigger.setAttribute("aria-expanded", "true");
      popover.hidden = false;
      search.value = "";
      applyFilter(root);
      search.focus();
    });
    search.addEventListener("input", () => applyFilter(root));
    search.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowDown") return;
      event.preventDefault();
      root.querySelector<HTMLElement>("[data-combobox-option]:not([hidden])")?.focus();
    });

    const options = [...root.querySelectorAll<HTMLElement>("[data-combobox-option]")];
    options.forEach((option) => {
      option.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
        event.preventDefault();
        const visible = options.filter((item) => !item.hidden);
        const current = visible.indexOf(option);
        const next = event.key === "ArrowDown" ? Math.min(current + 1, visible.length - 1) : Math.max(current - 1, 0);
        visible[next]?.focus();
      });
      option.addEventListener("click", () => {
        const currency = option.dataset.currencyValue;
        if (currency) {
          const select = root.querySelector("[data-currency-select]") as unknown as HTMLSelectElement | null;
          if (select) {
            select.value = currency;
            select.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
        close(root);
      });
    });
    root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      close(root, true);
    });
  });

  document.addEventListener("pointerdown", (event) => {
    roots.forEach((root) => {
      if (!root.contains(event.target as Node)) close(root);
    });
  });
  document.addEventListener("leobei:currency-change", (event) => {
    const currency = (event as CustomEvent<{ currency?: string }>).detail?.currency;
    if (!currency) return;
    const root = document.querySelector<HTMLElement>("[data-currency-combobox]");
    const selected = root?.querySelector<HTMLElement>(`[data-currency-value="${CSS.escape(currency)}"]`);
    if (!root || !selected) return;
    const symbol = selected.dataset.currencySymbol || currency;
    const triggerSymbol = root.querySelector<HTMLElement>("[data-selected-currency-symbol]");
    const triggerCode = root.querySelector<HTMLElement>("[data-selected-currency-code]");
    if (triggerSymbol) triggerSymbol.textContent = symbol;
    if (triggerCode) triggerCode.textContent = currency;
    root.querySelectorAll<HTMLElement>("[data-currency-value]").forEach((option) => {
      option.setAttribute("aria-selected", String(option.dataset.currencyValue === currency));
    });
  });
}

async function setupCurrency(): Promise<void> {
  const selectors = Array.from(document.querySelectorAll("[data-currency-select]")) as unknown as HTMLSelectElement[];
  if (!selectors.length) return;
  const supported = new Set([...selectors[0].options].map((option) => option.value));
  const remembered = localStorage.getItem(currencyStorageKey);
  const initial = remembered && supported.has(remembered) ? remembered : selectors[0].dataset.initialCurrency || "EUR";
  selectors.forEach((selector) => { selector.value = initial; });

  const notify = (currency: string) => {
    document.dispatchEvent(new CustomEvent("leobei:currency-change", { detail: { currency } }));
  };

  let rates: Record<string, number> | null = null;
  const update = async (currency: string) => {
    document.querySelectorAll<HTMLInputElement>("[data-quote-currency]").forEach((input) => { input.value = currency; });
    if (!rates) {
      try {
        const response = await fetch("/api/exchange-rates", { headers: { Accept: "application/json" } });
        if (!response.ok) throw new Error("rates unavailable");
        const payload = await response.json() as { rates?: Record<string, number> };
        rates = payload.rates ?? null;
      } catch {
        rates = null;
      }
    }
    document.querySelectorAll<HTMLElement>("[data-price-amount][data-price-currency]").forEach((price) => {
      const amount = Number(price.dataset.priceAmount);
      const source = price.dataset.priceCurrency || "EUR";
      if (!rates || !Number.isFinite(amount) || !rates[source] || !rates[currency]) {
        price.textContent = formatCurrency(amount, source);
        return;
      }
      price.textContent = formatCurrency((amount / rates[source]) * rates[currency], currency);
    });
  };
  selectors.forEach((selector) => selector.addEventListener("change", () => {
    try { localStorage.setItem(currencyStorageKey, selector.value); } catch { /* Storage can be unavailable in privacy mode. */ }
    selectors.forEach((other) => { other.value = selector.value; });
    notify(selector.value);
    void update(selector.value);
  }));
  notify(initial);
  await update(initial);
}

function setupNavigation(): void {
  const button = document.querySelector<HTMLButtonElement>("[data-mobile-menu-button]");
  const menu = document.querySelector<HTMLElement>("[data-mobile-menu]");
  button?.addEventListener("click", () => {
    const expanded = button.getAttribute("aria-expanded") === "true";
    button.setAttribute("aria-expanded", String(!expanded));
    if (menu) menu.hidden = expanded;
  });
  menu?.querySelectorAll("a").forEach((link) => link.addEventListener("click", () => {
    menu.hidden = true;
    button?.setAttribute("aria-expanded", "false");
  }));

}

function setupCatalogue(): void {
  const search = document.querySelector<HTMLInputElement>("[data-catalogue-search]");
  const filters = Array.from(document.querySelectorAll("[data-catalogue-filter]")) as unknown as HTMLSelectElement[];
  const filterDrawer = document.querySelector<HTMLDetailsElement>(".catalogue-filter-drawer");
  const narrowCatalogue = window.matchMedia("(max-width: 860px)");
  const syncFilterDrawer = (narrow: boolean) => {
    if (filterDrawer) filterDrawer.open = !narrow;
  };
  syncFilterDrawer(narrowCatalogue.matches);
  narrowCatalogue.addEventListener("change", (event) => syncFilterDrawer(event.matches));
  const cards = [...document.querySelectorAll<HTMLElement>("[data-vehicle-grid] [data-vehicle-card]")];
  const count = document.querySelector<HTMLElement>("[data-vehicle-count]");
  const filterCount = document.querySelector<HTMLElement>("[data-filter-count]");
  const empty = document.querySelector<HTMLElement>("[data-catalogue-empty]");
  const apply = () => {
    const query = search?.value.trim().toLocaleLowerCase() || "";
    let visible = 0;
    cards.forEach((card) => {
      const matchesSearch = !query || (card.dataset.search || "").includes(query);
      const matchesFilters = filters.every((filter) => {
        const key = filter.dataset.catalogueFilter;
        return !filter.value || (key && card.dataset[key] === filter.value);
      });
      card.hidden = !(matchesSearch && matchesFilters);
      if (!card.hidden) visible += 1;
    });
    if (count) count.textContent = String(visible);
    if (filterCount) {
      const activeFilters = filters.filter((filter) => Boolean(filter.value)).length + (query ? 1 : 0);
      filterCount.textContent = String(activeFilters);
      filterCount.hidden = activeFilters === 0;
    }
    if (empty) empty.hidden = visible !== 0;
  };
  search?.addEventListener("input", apply);
  filters.forEach((filter) => filter.addEventListener("change", apply));
  document.querySelector<HTMLButtonElement>("[data-filter-reset]")?.addEventListener("click", () => {
    if (search) search.value = "";
    filters.forEach((filter) => { filter.value = ""; });
    apply();
  });
}

function setupQuoteForm(): void {
  const form = document.querySelector<HTMLFormElement>("[data-quote-form]");
  if (!form) return;
  const status = form.querySelector<HTMLElement>("[data-quote-status]");
  const button = form.querySelector<HTMLButtonElement>("button[type=submit]");
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    const vehicleSelect = form.elements.namedItem("vehicleSlug") as HTMLSelectElement | null;
    const vehicleName = vehicleSelect?.selectedOptions[0]?.dataset.vehicleName || "";
    const payload = Object.fromEntries(data.entries()) as Record<string, FormDataEntryValue | boolean | number>;
    payload.vehicleName = vehicleName;
    payload.quantity = Number(payload.quantity || 1);
    payload.wantsVisit = data.get("wantsVisit") === "on";
    button?.setAttribute("disabled", "");
    if (button) button.textContent = button.dataset.sendingLabel || button.textContent;
    if (status) status.textContent = "";
    try {
      const response = await fetch("/api/vehicle-quotes", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error("quote failed");
      if (status) status.textContent = status.dataset.success || "OK";
      form.reset();
    } catch {
      if (status) status.textContent = status.dataset.error || "Error";
    } finally {
      button?.removeAttribute("disabled");
      if (button) button.textContent = button.dataset.submitLabel || button.textContent;
    }
  });
}

function setupGallery(): void {
  const main = document.querySelector<HTMLImageElement>("[data-gallery-main]");
  if (!main) return;
  document.querySelectorAll<HTMLButtonElement>("[data-gallery-thumb]").forEach((button) => {
    button.addEventListener("click", () => {
      const image = button.querySelector<HTMLImageElement>("img");
      if (!image) return;
      main.src = button.dataset.fullImage || image.src;
      main.alt = image.alt;
      document.querySelectorAll("[data-gallery-thumb]").forEach((item) => item.removeAttribute("aria-current"));
      button.setAttribute("aria-current", "true");
    });
  });
}

setupTheme();
setupNavigation();
setupComboboxes();
setupCatalogue();
setupQuoteForm();
setupGallery();
void setupCurrency();
